import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// components
import Radio from '@atoms/Radio';
import SubmitButton from '@atoms/SubmitButton';
import SelectCompanyRoster from '@atoms/SelectCompanyRoster';
import LoadingInline from '@molecules/LoadingInline';
import { H4 } from '@react/styledComponents';

// functionality
import useMembersOfCompany from '@hooks/apiLegacy/useMembersByCompany';
import useViewerCompanyIdEffective from '@hooks/useViewerCompanyIdEffective';
import useViewer from '@hooks/useViewer';
import { reasonsForExclusion } from '@modals/CartUpsellModalBase';
import useViewerCompanyNameEffective from '@hooks/useViewerCompanyNameEffective';

const RadioContainerStyled = styled.div`
    &:not(:last-child) {
        margin-bottom: 24px;
    }
`;

const SelectCompanyRosterContainerStyled = styled.div`
    max-width: 472px;
`;

const MemberSelection = ({ buttonText, headingText, membersToExclude, onSubmit }) => {
    // ------------------------------
    // State
    // ------------------------------

    const [meLabel, setMeLabel] = useState();
    const [linkToCart, setLinkToCart] = useState({});
    const [coworkersWithUpdatedLabels, setCoworkersWithUpdatedLabels] = useState();
    const { viewer } = useViewer();
    const { viewerCompanyId } = useViewerCompanyIdEffective();
    const { coworkers } = useMembersOfCompany(viewerCompanyId);
    const { viewerCompanyName } = useViewerCompanyNameEffective();
    // ------------------------------
    // Effects
    // ------------------------------

    // Update Me label if user is excluded
    useEffect(() => {
        if (!viewer || !membersToExclude) {
            return;
        }

        const meExcluded = membersToExclude.find(member => member.id === viewer.recordId);
        if (meExcluded) {
            const label = `Me (${meExcluded.reason})`;

            if (meExcluded.reason === reasonsForExclusion.ALREADY_IN_CART) {
                setLinkToCart({
                    text: label,
                    url: '/cart/view',
                });
            }

            setMeLabel(label);
        } else {
            setMeLabel('Me');
        }
    }, [viewer, membersToExclude]);

    // Update coworkers labels if they are excluded
    useEffect(() => {
        if (viewer && !viewer.company) {
            setCoworkersWithUpdatedLabels([]);
            return;
        }

        if (!coworkers || !membersToExclude) {
            return;
        }

        setCoworkersWithUpdatedLabels(
            coworkers.map(coworker => {
                const coworkerExcluded = membersToExclude.find(member => member.id === coworker.value);
                if (coworkerExcluded) {
                    return {
                        ...coworker,
                        label: `${coworker.label} (${coworkerExcluded.reason})`,
                    };
                }

                return coworker;
            })
        );
    }, [viewer, coworkers, membersToExclude]);

    // ------------------------------
    // Functions
    // ------------------------------

    const getIsSelectionInvalid = formValues => {
        if (!formValues.whoFor) {
            return true;
        }

        if (formValues.whoFor === 'me') {
            if (membersToExclude.some(member => member.id === viewer.recordId)) {
                return true;
            }
        }

        if (formValues.whoFor === 'coworker' && formValues.selectedCoworkers.length === 0) {
            return true;
        }

        return false;
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!viewer || !meLabel || !coworkersWithUpdatedLabels) {
        return <LoadingInline />;
    }

    return (
        <div>
            <H4 className="mb-1.5rem">{headingText || 'Who is this for?'}</H4>
            <Formik
                initialValues={{
                    whoFor: 'me',
                    selectedCoworkers: [],
                }}
                onSubmit={onSubmit}
            >
                {({ values, errors, setFieldValue }) => {
                    return (
                        <Form>
                            <div role="radiogroup" aria-labelledby="whoFor" aria-required>
                                <RadioContainerStyled>
                                    <Radio checked label={meLabel} linkLabel={linkToCart} name="whoFor" value="me" />
                                </RadioContainerStyled>

                                {viewer?.company && (
                                    <RadioContainerStyled>
                                        <Radio label="A Co-Worker" name="whoFor" value="coworker" />

                                        {values.whoFor === 'coworker' && viewerCompanyName && (
                                            <SelectCompanyRosterContainerStyled>
                                                <SelectCompanyRoster
                                                    companyName={viewerCompanyName}
                                                    name="selectCompanyRoster"
                                                    isOptionDisabled={option =>
                                                        membersToExclude.some(member => member.id === option.value)
                                                    }
                                                    onChange={options => setFieldValue('selectedCoworkers', options)}
                                                    options={coworkersWithUpdatedLabels}
                                                    placeholder="Select from company roster"
                                                />
                                            </SelectCompanyRosterContainerStyled>
                                        )}
                                    </RadioContainerStyled>
                                )}

                                <RadioContainerStyled>
                                    <Radio label="Someone new" name="whoFor" value="new" />
                                </RadioContainerStyled>
                            </div>

                            <SubmitButton isDisabled={getIsSelectionInvalid(values)}>
                                {buttonText || 'Continue'}
                            </SubmitButton>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

MemberSelection.propTypes = {
    buttonText: PropTypes.string,
    headingText: PropTypes.string,
    membersToExclude: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            reason: PropTypes.string.isRequired,
        })
    ).isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default MemberSelection;
